/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { get, map, toString } from 'lodash';
import * as Sentry from '@sentry/browser';
import { downloadFileFromAPI } from 'blb-utils/documents';
import { baseRequest } from './baseRequest';
const baseRoute = '/api/internal/loans';
export const getLoans = async () => baseRequest.get(`${baseRoute}/pipeline`);
export const getLoansElasticSearch = async query => baseRequest.post(`${baseRoute}/pipeline`, query);
export const searchForLoans = async query => baseRequest.get(`${baseRoute}/search`, {
  params: {
    searchTerm: query
  }
});
export const getDocuments = async loanId => baseRequest.get(`${baseRoute}/${loanId}/documents`);
export const getLoan = loanId => baseRequest.get(`${baseRoute}/${loanId}`);
export const getDocumentUrl = (loanId, documentId) => {
  return `${baseRoute}/${loanId}/documents/${documentId}`;
};
export const downloadDocument = async downloadDocParams => {
  const {
    loanId,
    documentId,
    documentTypeLabel,
    Toaster
  } = downloadDocParams;
  const url = getDocumentUrl(loanId, documentId);
  try {
    await downloadFileFromAPI(url, documentTypeLabel);
  } catch (e) {
    const err = new Error(`failed to download document for document: ${documentId} loan: ${loanId} Error: ${get(e, 'message', toString(e))}`);
    Sentry.captureException(err);
    Toaster?.addToast({
      message: 'Unable to download document',
      theme: 'error'
    });
  }
};
export const deleteDocument = async (loanId, documentId) => {
  await baseRequest.delete(getDocumentUrl(loanId, documentId));
};
export const putPipelineSaveEndpoint = async (endpoint, value) => await baseRequest.put(endpoint, {
  value
});
export const patchPipelineSaveEndpoint = async (endpoint, body) => await baseRequest.patch(endpoint, body);
export const getAvailableAssignees = async (endpoint, currentValue) => baseRequest.get(endpoint, {
  params: {
    currentPrincipalId: currentValue
  }
});
export const addDocs = _ref => {
  let {
    closingDesignation,
    documentType,
    files,
    loanId,
    sourceType
  } = _ref;
  const body = {
    closingDesignation,
    documents: map(files, _ref2 => {
      let {
        fileKey,
        fileName
      } = _ref2;
      return {
        fileKey,
        fileName
      };
    }),
    documentType,
    sourceType
  };
  return baseRequest.post(`${baseRoute}/${loanId}/docs`, body);
};
export const addDocsToLending = _ref3 => {
  let {
    files,
    loanId
  } = _ref3;
  return baseRequest.post(`${baseRoute}/${loanId}/documents`, {
    documents: files
  });
};
export const createCollatedDocument = async _ref4 => {
  let {
    collateFileName,
    files,
    loanId
  } = _ref4;
  const body = {
    fileName: collateFileName,
    files
  };
  return baseRequest.post(`${baseRoute}/${loanId}/multi-page`, body);
};
export const getDebugDataByLoanId = async (loanId, tenant) => {
  return baseRequest.get(`${baseRoute}/${loanId}/debug?tenant=${tenant}`);
};
export const getThreadsByLoanId = async loanId => {
  return baseRequest.get(`${baseRoute}/${loanId}/shared-inbox/threads`);
};
export const getThreadDetails = async (loanId, threadId) => {
  return baseRequest.get(`${baseRoute}/${loanId}/shared-inbox/thread/${threadId}`);
};
export const createThread = async (loanId, body) => {
  return baseRequest.post(`${baseRoute}/${loanId}/shared-inbox/thread`, body);
};
export const createMessage = async (loanId, threadId, body) => {
  return baseRequest.post(`${baseRoute}/${loanId}/shared-inbox/thread/${threadId}/message`, body);
};
export const getVersionedFees = async (loanId, tenant) => {
  return baseRequest.get(`${baseRoute}/${loanId}/fees?tenant=${tenant}`);
};
export const updateVersionedFees = async (loanId, tenant, fees) => {
  return baseRequest.put(`${baseRoute}/${loanId}/fees?tenant=${tenant}`, {
    fees
  });
};
export const createLock = async (loanId, userId, lockType, isLocked) => {
  const body = {
    isLocked,
    lockType,
    userId
  };
  return baseRequest.post(`${baseRoute}/${loanId}/lock`, body);
};